import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import {
  ClipboardList,
  Search,
  FileSignature,
  Ship,
  ShieldCheck,
  Truck,
} from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const PROCESS_BG =
  "https://images.unsplash.com/photo-1772300704502-410f0fbd43bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzgyODAzMjh8MA&ixlib=rb-4.1.0&q=85";

const STEP_META = [
  { icon: ClipboardList, n: "01" },
  { icon: Search, n: "02" },
  { icon: FileSignature, n: "03" },
  { icon: Ship, n: "04" },
  { icon: ShieldCheck, n: "05" },
  { icon: Truck, n: "06" },
];

export default function Process() {
  const { t } = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Background parallax
  const bgY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  // Timeline progress line (0->1)
  const lineProgress = useSpring(
    useTransform(scrollYProgress, [0.05, 0.85], [0, 1]),
    { stiffness: 100, damping: 30, restDelta: 0.001 }
  );
  const lineHeight = useTransform(lineProgress, (v) => `${v * 100}%`);

  return (
    <section
      id="process"
      ref={ref}
      data-testid="process-section"
      className="relative bg-[#F8F9FA] py-28 lg:py-40 overflow-hidden"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 will-change-transform pointer-events-none"
      >
        <img
          src={PROCESS_BG}
          alt=""
          aria-hidden="true"
          className="w-full h-[140%] object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F8F9FA] via-transparent to-[#F8F9FA]" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="overline">{t.process.overline}</span>
              <div className="h-px w-16 bg-slate-300" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.02] text-slate-900">
              {t.process.title1}<br />
              <span className="text-slate-400">{t.process.title2}</span>
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 lg:pt-6"
          >
            <p className="text-lg text-slate-600 leading-relaxed">
              {t.process.bodyPart1}{" "}
              <span className="text-slate-900 font-semibold">
                {t.process.bodyBold}
              </span>
              {t.process.bodyPart2}
            </p>
          </motion.div>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Static base line */}
          <div className="absolute left-[19px] md:left-1/2 md:-translate-x-1/2 top-3 bottom-3 w-[2px] bg-slate-200" />
          {/* Animated progress line */}
          <motion.div
            style={{ height: lineHeight }}
            className="absolute left-[19px] md:left-1/2 md:-translate-x-1/2 top-3 w-[2px] bg-gradient-to-b from-[#005BEA] to-[#FF6B00] origin-top"
          />

          <div className="space-y-16 md:space-y-24">
            {t.process.steps.map((step, i) => {
              const meta = STEP_META[i];
              const Icon = meta.icon;
              return (
              <motion.div
                key={meta.n}
                data-testid={`process-step-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: 0.05 }}
                className={`relative flex md:items-center gap-6 md:gap-12 ${
                  i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                {/* Dot */}
                <div className="absolute left-0 md:left-1/2 md:-translate-x-1/2 top-3 z-10">
                  <div className="relative w-10 h-10 rounded-full bg-white border-2 border-[#005BEA] flex items-center justify-center shadow-[0_4px_18px_rgba(0,91,234,0.25)] pulse-ring">
                    <Icon className="w-4 h-4 text-[#005BEA]" strokeWidth={2.4} />
                  </div>
                </div>

                {/* Content card */}
                <div className="ml-16 md:ml-0 md:w-1/2 md:px-8">
                  <div className="bg-white border border-slate-200 rounded-xl p-7 hover:shadow-[0_18px_50px_-15px_rgba(15,23,42,0.18)] hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-display text-xs font-bold text-[#005BEA] tracking-[0.18em]">
                        {t.process.stepLabel} {meta.n}
                      </span>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                    <h3 className="font-display text-2xl font-semibold text-slate-900 tracking-tight mb-3">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden md:block md:w-1/2" />
              </motion.div>
            );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
